import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Building2, Search, Users, LogOut, Globe, Eye, ExternalLink, Mail,
  Edit2, Save, X, ArrowLeft, Camera, User, Linkedin, Github, Youtube,
  MapPin, Briefcase, ChevronRight, Download, Star, FileSpreadsheet,
  Filter, GraduationCap, Award, Check, MessageSquare, Loader2, CheckCircle2, Clock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { MessagesPanel } from '../components/MessagesPanel';
import { timeAgo, freshness } from '../lib/timeAgo';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const countries = ['All Countries', 'South Africa', 'Nigeria', 'Kenya', 'Ghana', 'Egypt', 'Botswana', 'Zimbabwe', 'Tanzania', 'Uganda'];
const fieldsOfStudy = ['All Fields', 'Computer Science', 'Engineering', 'Business', 'Finance', 'Law', 'Medicine', 'Education', 'Arts', 'Sciences', 'Other'];
const levelsOfStudy = ['All Levels', 'Certificate', 'Diploma', 'Bachelors', 'Honours', 'Masters', 'Doctorate', 'PhD'];

const portfolioPlatformNames = {
  github: 'GitHub', linkedin: 'LinkedIn', youtube: 'YouTube', behance: 'Behance',
  dribbble: 'Dribbble', orcid: 'ORCID', google_drive: 'Google Drive', portfolio: 'Website',
};

function getAuthHeaders() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function ensureAbsoluteUrl(url) {
  if (!url) return url;
  if (url.match(/^https?:\/\//i)) return url;
  return `https://${url}`;
}

// Candidate profile view modal
function CandidateProfileModal({ candidate, open, onClose }) {
  if (!candidate) return null;

  const handleDownloadPDF = () => {
    const c = candidate;
    const eduHtml = (c.education || []).length > 0 ? `<div class="s"><div class="st">Education / Training</div>${c.education.map(e => `<div class="cd" style="border-left-color:${e.type === 'tertiary' ? '#01354d' : '#c89c3a'}"><strong>${e.institution || ''}</strong>${e.qualification ? `<br>${e.qualification}${e.field_of_study ? ` in ${e.field_of_study}` : ''}` : ''}${e.start_year ? `<br><small>${e.start_year}${e.end_year ? ` - ${e.end_year}` : ''}</small>` : ''}</div>`).join('')}</div>` : '';
    const expHtml = (c.experience || []).length > 0 ? `<div class="s"><div class="st">Experience</div>${c.experience.map(e => `<div class="cd" style="border-left-color:#dc2626"><strong>${e.role || ''}</strong>${e.company ? `<br>${e.company}` : ''}${e.location ? ` - ${e.location}` : ''}${e.start_date ? `<br><small>${e.start_date}${e.is_current ? ' - Present' : (e.end_date ? ` - ${e.end_date}` : '')}</small>` : ''}${e.responsibilities ? `<br><small>${e.responsibilities}</small>` : ''}</div>`).join('')}</div>` : '';
    const skillsHtml = (c.skills || []).length > 0 ? `<div class="s"><div class="st">Skills & References</div>${c.skills.map(s => `<div class="cd" style="border-left-color:#c89c3a"><strong>${typeof s === 'string' ? s : s.name}</strong>${s.reference?.name ? `<br><small>Ref: ${s.reference.name} ${s.reference.surname || ''} - ${s.reference.title || ''} at ${s.reference.organisation || ''}</small>` : ''}${s.reference?.email ? `<br><small>${s.reference.email}${s.reference.phone ? ` | ${s.reference.phone}` : ''}</small>` : ''}</div>`).join('')}</div>` : '';
    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>${c.name} - MeritumPraxis</title><style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:'Segoe UI',Tahoma,Geneva,Verdana,sans-serif;padding:40px;max-width:800px;margin:0 auto;color:#062240}.download-bar{position:fixed;top:0;left:0;right:0;background:#01354d;color:white;padding:12px 20px;display:flex;justify-content:space-between;align-items:center;z-index:1000}.dl-btn{background:#c89c3a;color:#01354d;border:none;padding:8px 20px;border-radius:4px;cursor:pointer;font-weight:bold}.content{margin-top:60px}.header{text-align:center;margin-bottom:30px;padding-bottom:20px;border-bottom:3px solid #c89c3a}.pp{width:120px;height:120px;border-radius:50%;margin:0 auto 20px;display:block;object-fit:cover;border:3px solid #c89c3a}.s{margin-bottom:25px}.st{font-size:18px;font-weight:bold;color:#01354d;margin-bottom:10px;padding-bottom:5px;border-bottom:2px solid #01354d}.cd{margin-bottom:10px;padding:10px;background:#f8f9fa;border-left:3px solid #01354d}.ft{margin-top:40px;padding-top:20px;border-top:2px solid #ddd;text-align:center;font-size:12px;color:#666}@media print{.download-bar{display:none!important}.content{margin-top:0}body{padding:20px}}</style></head><body><div class="download-bar"><span>${c.name} - MeritumPraxis</span><button class="dl-btn" onclick="window.print()">Download PDF</button></div><div class="content"><div class="header"><div style="font-size:24px;font-weight:bold;color:#01354d">MeritumPraxis</div><div style="font-size:12px;color:#666">Progress Made Visible</div></div>${c.profile_picture ? `<img src="${c.profile_picture}" class="pp">` : ''}<div style="font-size:28px;font-weight:bold;color:#01354d;text-align:center">${c.name}</div>${c.headline ? `<div style="font-size:16px;color:#c89c3a;text-align:center;margin-top:5px">${c.headline}</div>` : ''}${(c.city || c.country) ? `<div style="font-size:14px;color:#666;text-align:center;margin-top:5px">${c.city || ''}${c.city && c.country ? ', ' : ''}${c.country || ''}</div>` : ''}<div class="s"><div class="st">Contact</div><p><strong>Email:</strong> ${c.email}</p>${c.gender ? `<p><strong>Gender:</strong> ${c.gender}</p>` : ''}</div>${c.bio ? `<div class="s"><div class="st">About</div><p style="line-height:1.6">${c.bio}</p></div>` : ''}${eduHtml}${expHtml}${skillsHtml}${c.portfolio_links && Object.entries(c.portfolio_links).filter(([,v])=>v).length ? `<div class="s"><div class="st">Portfolio</div>${Object.entries(c.portfolio_links).filter(([,v])=>v).map(([k,v])=>`<div class="cd" style="border-left-color:#0e7490"><strong>${portfolioPlatformNames[k]||k}:</strong> ${v}</div>`).join('')}</div>` : ''}<div class="ft"><p>MeritumPraxis - Progress Made Visible</p></div></div></body></html>`;
    const w = window.open('', '_blank');
    if (w) { w.document.write(html); w.document.close(); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-meritum-navy">{candidate.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {/* Header */}
          <div className="flex items-center gap-4 pb-4 border-b border-slate-100">
            <div className="w-16 h-16 rounded-full bg-meritum-teal/10 flex items-center justify-center overflow-hidden">
              {candidate.profile_picture ? <img src={candidate.profile_picture} alt="" className="w-full h-full object-cover" /> : <User className="text-meritum-teal" size={28} />}
            </div>
            <div>
              <h2 className="font-heading text-lg font-semibold text-meritum-navy">{candidate.name}</h2>
              {candidate.headline && <p className="font-body text-meritum-teal">{candidate.headline}</p>}
              {(candidate.city || candidate.country) && <p className="font-body text-sm text-slate-500">{candidate.city}{candidate.city && candidate.country ? ', ' : ''}{candidate.country}</p>}
            </div>
          </div>

          {/* Bio */}
          {candidate.bio && <div><h3 className="font-heading text-base font-semibold text-meritum-navy mb-1">About</h3><p className="font-body text-sm text-slate-600 whitespace-pre-wrap">{candidate.bio}</p></div>}

          {/* Skills */}
          {candidate.skills?.length > 0 && (
            <div>
              <h3 className="font-heading text-base font-semibold text-meritum-navy mb-2">Skills</h3>
              <div className="flex flex-wrap gap-2">
                {candidate.skills.map((skill, i) => (
                  <span key={i} className="px-3 py-1 bg-meritum-gold/10 text-meritum-gold rounded-full text-sm font-body">{typeof skill === 'string' ? skill : skill.name}</span>
                ))}
              </div>
            </div>
          )}

          {/* Education */}
          {candidate.education?.length > 0 && (
            <div>
              <h3 className="font-heading text-base font-semibold text-meritum-navy mb-2">Education</h3>
              <div className="space-y-3">
                {candidate.education.map((edu, i) => (
                  <div key={i} className={`p-3 rounded-lg border ${edu.type === 'tertiary' ? 'border-meritum-teal/20 bg-meritum-teal/5' : 'border-meritum-gold/20 bg-meritum-gold/5'}`}>
                    <p className="font-body text-xs font-medium uppercase tracking-wide text-slate-400 mb-1">{edu.type === 'tertiary' ? 'Tertiary' : 'High School'}</p>
                    <p className="font-heading font-semibold text-meritum-navy text-sm">{edu.institution}</p>
                    {(edu.qualification || edu.field_of_study) && <p className="font-body text-sm text-slate-600">{edu.qualification}{edu.qualification && edu.field_of_study ? ' in ' : ''}{edu.field_of_study}</p>}
                    {(edu.start_year || edu.end_year) && <p className="font-body text-xs text-slate-400">{edu.start_year}{edu.start_year && edu.end_year ? ' - ' : ''}{edu.end_year}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Experience */}
          {candidate.experience?.length > 0 && (
            <div>
              <h3 className="font-heading text-base font-semibold text-meritum-navy mb-2">Experience</h3>
              <div className="space-y-3">
                {candidate.experience.map((exp, i) => (
                  <div key={i} className="p-3 rounded-lg border border-rose-200 bg-rose-50/50">
                    <p className="font-heading font-semibold text-meritum-navy text-sm">{exp.role}</p>
                    {exp.company && <p className="font-body text-sm text-meritum-teal">{exp.company}{exp.location ? ` - ${exp.location}` : ''}</p>}
                    {exp.start_date && <p className="font-body text-xs text-slate-400">{exp.start_date}{exp.is_current ? ' - Present' : (exp.end_date ? ` - ${exp.end_date}` : '')}</p>}
                    {exp.responsibilities && <p className="font-body text-xs text-slate-600 mt-1">{exp.responsibilities}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* References */}
          {candidate.skills?.some(s => s.reference?.name) && (
            <div>
              <h3 className="font-heading text-base font-semibold text-meritum-navy mb-2">References</h3>
              <div className="space-y-2">
                {candidate.skills.filter(s => s.reference?.name).map((s, i) => (
                  <div key={i} className="p-3 rounded-lg border border-meritum-gold/20 bg-meritum-gold/5">
                    <p className="font-body text-xs font-medium text-slate-400">For: {s.name}</p>
                    <p className="font-heading font-semibold text-meritum-navy text-sm">{s.reference.name} {s.reference.surname || ''}</p>
                    {s.reference.title && <p className="font-body text-sm text-slate-600">{s.reference.title}{s.reference.organisation ? ` at ${s.reference.organisation}` : ''}</p>}
                    <div className="flex gap-4 mt-1">
                      {s.reference.email && <span className="font-body text-xs text-slate-400">{s.reference.email}</span>}
                      {s.reference.phone && <span className="font-body text-xs text-slate-400">{s.reference.phone}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Portfolio */}
          {candidate.portfolio_links && Object.entries(candidate.portfolio_links).filter(([,v]) => v).length > 0 && (
            <div>
              <h3 className="font-heading text-base font-semibold text-meritum-navy mb-2">Portfolio</h3>
              <div className="space-y-2">
                {Object.entries(candidate.portfolio_links).filter(([,v]) => v).map(([key, url]) => (
                  <a key={key} href={ensureAbsoluteUrl(url)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 p-2 rounded-lg bg-cyan-50 border border-cyan-100 hover:bg-cyan-100 transition">
                    <ExternalLink size={14} className="text-cyan-700" />
                    <span className="font-body text-sm text-cyan-700">{portfolioPlatformNames[key] || key}: {url}</span>
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-2 border-t border-slate-100">
            <Button onClick={handleDownloadPDF} className="bg-meritum-gold text-meritum-navy hover:bg-meritum-gold/90 font-body" data-testid="modal-download-pdf-btn">
              <Download size={16} className="mr-2" />Download PDF
            </Button>
            <a href={`mailto:${candidate.email}`}>
              <Button variant="outline" className="border-meritum-teal text-meritum-teal hover:bg-meritum-teal hover:text-white font-body" data-testid="modal-email-btn">
                <Mail size={16} className="mr-2" />Send Email
              </Button>
            </a>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function OrganizationDashboard() {
  const navigate = useNavigate();
  const logoInputRef = useRef(null);
  const autosaveTimer = useRef(null);
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('candidates');

  // Org profile state
  const [orgProfile, setOrgProfile] = useState({
    company_name: '', industry: '', description: '', website: '',
    country: '', city: '', company_size: '', logo: null,
    linkedin_url: '', twitter_url: '', facebook_url: '',
  });
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [autosaveStatus, setAutosaveStatus] = useState('');
  const [profileMsg, setProfileMsg] = useState('');

  // Candidate browsing state
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('All Countries');
  const [selectedField, setSelectedField] = useState('All Fields');
  const [selectedLevel, setSelectedLevel] = useState('All Levels');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [isLoadingCandidates, setIsLoadingCandidates] = useState(false);
  const [visibleCount, setVisibleCount] = useState(50);

  // Shortlist state
  const [shortlistedIds, setShortlistedIds] = useState(new Set());
  const [isExporting, setIsExporting] = useState(false);

  // Candidate profile modal
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (!storedUser) { navigate('/login'); return; }
    const userData = JSON.parse(storedUser);
    if (userData.user_type !== 'organization') { navigate('/dashboard'); return; }
    setUser(userData);
    loadOrgProfile();
    loadCandidates();
    loadShortlist();
  }, [navigate]);

  const loadOrgProfile = async () => {
    try {
      const res = await axios.get(`${API}/profile`, { headers: getAuthHeaders() });
      if (res.data && res.data.exists !== false) {
        setOrgProfile({
          company_name: res.data.company_name || '',
          industry: res.data.industry || '',
          description: res.data.description || '',
          website: res.data.website || '',
          country: res.data.country || '',
          city: res.data.city || '',
          company_size: res.data.company_size || '',
          logo: res.data.logo || null,
          linkedin_url: res.data.linkedin_url || '',
          twitter_url: res.data.twitter_url || '',
          facebook_url: res.data.facebook_url || '',
        });
      }
    } catch (err) { console.error('Failed to load org profile:', err); }
  };

  const handleSaveOrgProfile = async () => {
    setIsSavingProfile(true);
    try {
      await axios.put(`${API}/profile`, orgProfile, { headers: getAuthHeaders() });
      setProfileMsg('Profile saved successfully!');
      setIsEditingProfile(false);
      setTimeout(() => setProfileMsg(''), 3000);
    } catch (err) {
      console.error('Failed to save profile:', err);
      alert('Failed to save profile.');
    } finally { setIsSavingProfile(false); }
  };

  // Autosave: debounce 2s after last change while editing
  useEffect(() => {
    if (!isEditingProfile || !user) return;
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(async () => {
      try {
        setAutosaveStatus('saving');
        await axios.put(`${API}/profile`, orgProfile, { headers: getAuthHeaders() });
        setAutosaveStatus('saved');
        setTimeout(() => setAutosaveStatus(''), 2000);
      } catch (err) {
        setAutosaveStatus('');
      }
    }, 2000);
    return () => { if (autosaveTimer.current) clearTimeout(autosaveTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgProfile, isEditingProfile]);

  const handleLogoUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { alert('Logo size should be less than 5MB'); return; }
    const reader = new FileReader();
    reader.onloadend = () => { setOrgProfile(p => ({ ...p, logo: reader.result })); };
    reader.readAsDataURL(file);
  };

  const loadCandidates = async (search = '', country = '', field = '', level = '') => {
    setIsLoadingCandidates(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (country && country !== 'All Countries') params.country = country;
      if (field && field !== 'All Fields') params.field_of_study = field;
      if (level && level !== 'All Levels') params.level_of_study = level;
      const res = await axios.get(`${API}/candidates/browse`, { headers: getAuthHeaders(), params });
      setCandidates(res.data || []);
    } catch (err) {
      console.error('Failed to load candidates:', err);
      setCandidates([]);
    } finally { setIsLoadingCandidates(false); }
  };

  const loadShortlist = async () => {
    try {
      const res = await axios.get(`${API}/shortlist`, { headers: getAuthHeaders() });
      setShortlistedIds(new Set(res.data || []));
    } catch (err) { console.error('Failed to load shortlist:', err); }
  };

  const toggleShortlist = async (candidateId) => {
    const isShortlisted = shortlistedIds.has(candidateId);
    try {
      if (isShortlisted) {
        await axios.delete(`${API}/shortlist/${candidateId}`, { headers: getAuthHeaders() });
        setShortlistedIds(prev => { const n = new Set(prev); n.delete(candidateId); return n; });
      } else {
        await axios.post(`${API}/shortlist/${candidateId}`, {}, { headers: getAuthHeaders() });
        setShortlistedIds(prev => new Set(prev).add(candidateId));
      }
    } catch (err) { console.error('Failed to toggle shortlist:', err); }
  };

  const handleExportCSV = async () => {
    setIsExporting(true);
    try {
      const res = await axios.get(`${API}/shortlist/export`, {
        headers: getAuthHeaders(), responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `shortlisted_candidates_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
      alert(err.response?.status === 400 ? 'Shortlist is empty. Add candidates first.' : 'Export failed.');
    } finally { setIsExporting(false); }
  };

  const handleSearch = () => {
    loadCandidates(searchTerm, selectedCountry, selectedField, selectedLevel);
  };

  useEffect(() => {
    if (user) loadCandidates(searchTerm, selectedCountry, selectedField, selectedLevel);
  }, [selectedCountry, selectedField, selectedLevel]);

  const viewCandidateProfile = (candidateId) => {
    window.open(`/candidate-view/${candidateId}`, '_blank');
  };

  const handleLogout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    navigate('/login');
  };

  const shortlistedCandidates = candidates.filter(c => shortlistedIds.has(c.id));

  if (!user) return null;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="org-dashboard">
      {/* Dashboard Header */}
      <div className="bg-gradient-to-r from-meritum-navy to-meritum-teal text-white py-6 sm:py-8">
        <div className="container mx-auto px-3 sm:px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="flex items-center justify-between mb-2 gap-2">
            <Link to="/" className="flex items-center gap-2 text-white/70 hover:text-white font-body text-xs sm:text-sm transition" data-testid="back-to-home">
              <ArrowLeft size={16} /><span className="hidden sm:inline">Back to Home</span>
            </Link>
            <Button onClick={handleLogout} variant="ghost" size="sm" className="text-white/80 hover:text-white hover:bg-white/10 font-body px-2 sm:px-3" data-testid="logout-btn">
              <LogOut size={16} className="sm:mr-2" /><span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="w-12 h-12 sm:w-16 sm:h-16 bg-white/10 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/20 overflow-hidden flex-shrink-0">
              {orgProfile.logo ? <img src={orgProfile.logo} alt="Logo" className="w-full h-full object-cover" /> : <Building2 className="text-white" size={24} />}
            </div>
            <div className="min-w-0">
              <h1 className="font-heading text-lg sm:text-2xl font-bold truncate">{orgProfile.company_name || user.name}</h1>
              <p className="font-body text-white/70 text-xs sm:text-sm truncate">{orgProfile.industry || 'Organization Dashboard'}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-8 bg-white shadow-sm" data-testid="dashboard-tabs">
            <TabsTrigger value="candidates" className="font-body data-[state=active]:bg-meritum-teal data-[state=active]:text-white" data-testid="tab-candidates">
              <Users size={16} className="mr-2" />Browse Candidates
            </TabsTrigger>
            <TabsTrigger value="shortlist" className="font-body data-[state=active]:bg-meritum-gold data-[state=active]:text-meritum-navy" data-testid="tab-shortlist">
              <Star size={16} className="mr-2" />Shortlist ({shortlistedIds.size})
            </TabsTrigger>
            <TabsTrigger value="messages" className="font-body data-[state=active]:bg-meritum-teal data-[state=active]:text-white" data-testid="tab-messages">
              <MessageSquare size={16} className="mr-2" />Messages
            </TabsTrigger>
            <TabsTrigger value="profile" className="font-body data-[state=active]:bg-meritum-teal data-[state=active]:text-white" data-testid="tab-profile">
              <Building2 size={16} className="mr-2" />Organization Profile
            </TabsTrigger>
          </TabsList>

          {/* Candidates Tab */}
          <TabsContent value="candidates">
            {/* Search and Filters */}
            <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <Input
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    placeholder="Search by name, skills, or headline..."
                    className="pl-10"
                    data-testid="search-input"
                  />
                </div>
                <div className="flex gap-3">
                  <Select value={selectedCountry} onValueChange={setSelectedCountry}>
                    <SelectTrigger className="w-44" data-testid="country-filter">
                      <Globe size={16} className="mr-2 text-slate-400" />
                      <SelectValue placeholder="Country" />
                    </SelectTrigger>
                    <SelectContent>
                      {countries.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button onClick={() => setShowAdvancedFilters(!showAdvancedFilters)} variant="outline" className="border-meritum-navy/20 text-meritum-navy font-body" data-testid="toggle-advanced-filters">
                    <Filter size={16} className="mr-2" />{showAdvancedFilters ? 'Hide' : 'More'} Filters
                  </Button>
                  <Button onClick={handleSearch} className="bg-meritum-teal text-white hover:bg-meritum-navy font-body" data-testid="search-btn">
                    <Search size={16} className="mr-2" />Search
                  </Button>
                </div>
              </div>

              {/* Advanced Filters */}
              {showAdvancedFilters && (
                <div className="flex flex-col md:flex-row gap-4 mt-4 pt-4 border-t border-slate-100" data-testid="advanced-filters-panel">
                  <Select value={selectedField} onValueChange={setSelectedField}>
                    <SelectTrigger className="w-48" data-testid="field-filter">
                      <GraduationCap size={16} className="mr-2 text-slate-400" />
                      <SelectValue placeholder="Field of Study" />
                    </SelectTrigger>
                    <SelectContent>
                      {fieldsOfStudy.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={selectedLevel} onValueChange={setSelectedLevel}>
                    <SelectTrigger className="w-48" data-testid="level-filter">
                      <Award size={16} className="mr-2 text-slate-400" />
                      <SelectValue placeholder="Level of Study" />
                    </SelectTrigger>
                    <SelectContent>
                      {levelsOfStudy.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {/* Results count */}
            <div className="flex items-center justify-between mb-6">
              <p className="font-body text-slate-600" data-testid="results-count">
                {isLoadingCandidates ? 'Loading...' : `Showing ${candidates.length} candidate${candidates.length !== 1 ? 's' : ''}`}
              </p>
              {shortlistedIds.size > 0 && (
                <p className="font-body text-sm text-meritum-gold font-medium" data-testid="shortlist-count-label">
                  <Star size={14} className="inline mr-1" />{shortlistedIds.size} shortlisted
                </p>
              )}
            </div>

            {/* Candidate Cards */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="candidate-grid">
              {candidates.slice(0, visibleCount).map((candidate) => {
                const isShortlisted = shortlistedIds.has(candidate.id);
                return (
                  <div key={candidate.id} className={`bg-white rounded-xl shadow-sm border p-6 hover:shadow-md transition-shadow duration-200 ${isShortlisted ? 'border-meritum-gold ring-1 ring-meritum-gold/30' : 'border-slate-100'}`} data-testid={`candidate-card-${candidate.id}`}>
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-meritum-teal/10 rounded-full flex items-center justify-center overflow-hidden">
                          {candidate.profile_picture ? (
                            <img src={candidate.profile_picture} alt={candidate.name} className="w-full h-full object-cover" />
                          ) : (
                            <User className="text-meritum-teal" size={20} />
                          )}
                        </div>
                        <div>
                          <h3 className="font-heading text-lg font-semibold text-meritum-navy">{candidate.name}</h3>
                          <p className="font-body text-sm text-slate-500">{candidate.headline || 'No headline'}</p>
                        </div>
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleShortlist(candidate.id); }}
                        className={`p-2 rounded-full transition-colors ${isShortlisted ? 'bg-meritum-gold/10 text-meritum-gold' : 'hover:bg-slate-100 text-slate-300'}`}
                        data-testid={`shortlist-toggle-${candidate.id}`}
                        title={isShortlisted ? 'Remove from shortlist' : 'Add to shortlist'}
                      >
                        <Star size={18} fill={isShortlisted ? 'currentColor' : 'none'} />
                      </button>
                    </div>

                    {(candidate.city || candidate.country) && (
                      <div className="flex items-center gap-2 text-sm text-slate-500 mb-4">
                        <Globe size={14} />
                        <span>{candidate.city}{candidate.city && candidate.country ? ', ' : ''}{candidate.country}</span>
                      </div>
                    )}

                    <div className="flex flex-wrap gap-1 mb-4">
                      {(candidate.skills || []).slice(0, 3).map((skill, index) => (
                        <span key={index} className="px-2 py-1 bg-meritum-gold/10 text-meritum-gold text-xs rounded">
                          {typeof skill === 'string' ? skill : skill.name}
                        </span>
                      ))}
                      {(candidate.skills || []).length > 3 && (
                        <span className="px-2 py-1 bg-slate-100 text-slate-500 text-xs rounded">+{candidate.skills.length - 3}</span>
                      )}
                    </div>

                    {candidate.updated_at && (
                      <div
                        className={`mb-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-body text-[10px] ${
                          freshness(candidate.updated_at) === 'fresh' ? 'bg-emerald-50 text-emerald-700' :
                          freshness(candidate.updated_at) === 'stale' ? 'bg-amber-50 text-amber-700' :
                          'bg-slate-100 text-slate-500'
                        }`}
                        data-testid={`card-last-updated-${candidate.id}`}
                      >
                        <Clock size={10} />Updated {timeAgo(candidate.updated_at)}
                      </div>
                    )}

                    <Button
                      onClick={() => viewCandidateProfile(candidate.id)}
                      variant="outline"
                      className="w-full border-meritum-teal text-meritum-teal hover:bg-meritum-teal hover:text-white font-body text-sm"
                      data-testid={`view-profile-btn-${candidate.id}`}
                    >
                      <Eye size={14} className="mr-2" />View Profile
                    </Button>
                  </div>
                );
              })}
            </div>

            {!isLoadingCandidates && candidates.length === 0 && (
              <div className="text-center py-16">
                <Users className="mx-auto text-slate-300" size={48} />
                <h3 className="font-heading text-xl font-semibold text-meritum-navy mt-4 mb-2">No candidates found</h3>
                <p className="font-body text-slate-500">Try adjusting your search or filters, or check back later as more candidates join.</p>
              </div>
            )}

            {/* Pagination control */}
            {candidates.length > 0 && (
              <div className="mt-8 text-center" data-testid="pagination-control">
                <p className="font-body text-sm text-slate-500 mb-3">
                  Showing <strong>{Math.min(visibleCount, candidates.length)}</strong> of <strong>{candidates.length}</strong> candidates
                </p>
                {candidates.length > visibleCount && (
                  <Button onClick={() => setVisibleCount((v) => v + 50)} variant="outline" className="border-meritum-navy text-meritum-navy hover:bg-meritum-navy hover:text-white font-body" data-testid="load-more-btn">
                    Show next 50 candidates
                  </Button>
                )}
              </div>
            )}

            {/* Pricing CTA */}
            <div className="mt-12 bg-meritum-gold/10 border border-meritum-gold/20 rounded-xl p-8 text-center">
              <h3 className="font-heading text-2xl font-semibold text-meritum-navy mb-2">Unlock Full Access</h3>
              <p className="font-body text-slate-600 mb-6 max-w-xl mx-auto">Contact us for custom organization pricing to access advanced search features and direct messaging.</p>
              <Link to="/contact"><Button className="bg-meritum-gold text-meritum-navy hover:bg-meritum-gold/90 font-body font-bold">Contact for Pricing</Button></Link>
            </div>
          </TabsContent>

          {/* Shortlist Tab */}
          <TabsContent value="shortlist">
            <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="font-heading text-xl font-semibold text-meritum-navy flex items-center gap-2">
                    <Star size={20} className="text-meritum-gold" />Shortlisted Candidates
                  </h2>
                  <p className="font-body text-sm text-slate-500 mt-1">{shortlistedIds.size} candidate{shortlistedIds.size !== 1 ? 's' : ''} in your shortlist</p>
                </div>
                {shortlistedIds.size > 0 && (
                  <Button
                    onClick={handleExportCSV}
                    disabled={isExporting}
                    className="bg-meritum-navy text-white hover:bg-meritum-navy/90 font-body"
                    data-testid="export-csv-btn"
                  >
                    <FileSpreadsheet size={16} className="mr-2" />
                    {isExporting ? 'Exporting...' : 'Export CSV'}
                  </Button>
                )}
              </div>
            </div>

            {shortlistedCandidates.length > 0 ? (
              <div className="space-y-4">
                {shortlistedCandidates.map((candidate) => (
                  <div key={candidate.id} className="bg-white rounded-xl shadow-sm border border-meritum-gold/20 p-6 flex items-center gap-6" data-testid={`shortlist-card-${candidate.id}`}>
                    <div className="w-14 h-14 bg-meritum-teal/10 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0">
                      {candidate.profile_picture ? <img src={candidate.profile_picture} alt="" className="w-full h-full object-cover" /> : <User className="text-meritum-teal" size={24} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-heading text-lg font-semibold text-meritum-navy">{candidate.name}</h3>
                      <p className="font-body text-sm text-slate-500 truncate">{candidate.headline || 'No headline'}</p>
                      <div className="flex items-center gap-4 mt-1">
                        {(candidate.city || candidate.country) && (
                          <span className="font-body text-xs text-slate-400 flex items-center gap-1"><Globe size={12} />{candidate.city}{candidate.city && candidate.country ? ', ' : ''}{candidate.country}</span>
                        )}
                        {candidate.email && <span className="font-body text-xs text-slate-400 flex items-center gap-1"><Mail size={12} />{candidate.email}</span>}
                      </div>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {(candidate.skills || []).slice(0, 5).map((s, i) => (
                          <span key={i} className="px-2 py-0.5 bg-meritum-gold/10 text-meritum-gold text-xs rounded">{typeof s === 'string' ? s : s.name}</span>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Button onClick={() => viewCandidateProfile(candidate.id)} variant="outline" size="sm" className="border-meritum-teal text-meritum-teal hover:bg-meritum-teal hover:text-white font-body" data-testid={`shortlist-view-${candidate.id}`}>
                        <Eye size={14} className="mr-1" />View
                      </Button>
                      <a href={`mailto:${candidate.email}`}>
                        <Button variant="outline" size="sm" className="border-meritum-gold text-meritum-gold hover:bg-meritum-gold hover:text-meritum-navy font-body" data-testid={`shortlist-email-${candidate.id}`}>
                          <Mail size={14} className="mr-1" />Email
                        </Button>
                      </a>
                      <button onClick={() => toggleShortlist(candidate.id)} className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-full transition" data-testid={`shortlist-remove-${candidate.id}`} title="Remove from shortlist">
                        <X size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <Star className="mx-auto text-slate-300" size={48} />
                <h3 className="font-heading text-xl font-semibold text-meritum-navy mt-4 mb-2">No shortlisted candidates</h3>
                <p className="font-body text-slate-500">Browse candidates and click the star icon to add them to your shortlist.</p>
                <Button onClick={() => setActiveTab('candidates')} className="mt-4 bg-meritum-teal text-white hover:bg-meritum-navy font-body" data-testid="go-browse-btn">
                  <Users size={16} className="mr-2" />Browse Candidates
                </Button>
              </div>
            )}
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages">
            <div className="bg-white rounded-xl shadow-sm p-6">
              <MessagesPanel currentUserId={user.id} />
            </div>
          </TabsContent>

          {/* Organization Profile Tab */}
          <TabsContent value="profile">
            {profileMsg && (
              <div className="bg-green-100 border border-green-200 rounded-lg py-3 px-4 mb-6" data-testid="profile-success-msg">
                <p className="text-green-700 font-body text-sm">{profileMsg}</p>
              </div>
            )}
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Left - Summary Card */}
              <div className="lg:col-span-1">
                <div className="bg-white rounded-xl shadow-sm p-6 sticky top-24">
                  <div className="text-center mb-6">
                    <div className="relative inline-block">
                      <div className="w-24 h-24 rounded-lg bg-meritum-teal/10 flex items-center justify-center mx-auto border-4 border-meritum-gold/30 overflow-hidden">
                        {orgProfile.logo ? <img src={orgProfile.logo} alt="Logo" className="w-full h-full object-cover" /> : <Building2 size={40} className="text-meritum-teal" />}
                      </div>
                      {isEditingProfile && (
                        <>
                          <input type="file" ref={logoInputRef} onChange={handleLogoUpload} accept="image/*" className="hidden" data-testid="org-logo-input" />
                          <button onClick={() => logoInputRef.current?.click()} className="absolute bottom-0 right-0 w-8 h-8 bg-meritum-gold rounded-full flex items-center justify-center shadow-lg hover:bg-meritum-gold/90 transition-colors" data-testid="upload-logo-btn">
                            <Camera size={14} className="text-meritum-navy" />
                          </button>
                        </>
                      )}
                    </div>
                    <h2 className="font-heading text-xl font-semibold text-meritum-navy mt-4">{orgProfile.company_name || user.name}</h2>
                    {orgProfile.industry && <p className="font-body text-meritum-teal font-medium mt-1">{orgProfile.industry}</p>}
                    {(orgProfile.city || orgProfile.country) && (
                      <p className="font-body text-xs text-slate-400 mt-1 flex items-center justify-center gap-1">
                        <MapPin size={12} />{orgProfile.city}{orgProfile.city && orgProfile.country ? ', ' : ''}{orgProfile.country}
                      </p>
                    )}
                    {/* Social Links Display */}
                    {!isEditingProfile && (orgProfile.linkedin_url || orgProfile.twitter_url || orgProfile.facebook_url) && (
                      <div className="flex items-center justify-center gap-3 mt-3">
                        {orgProfile.linkedin_url && <a href={ensureAbsoluteUrl(orgProfile.linkedin_url)} target="_blank" rel="noopener noreferrer" className="text-meritum-teal hover:text-meritum-navy transition" data-testid="org-linkedin-link"><Linkedin size={18} /></a>}
                        {orgProfile.twitter_url && <a href={ensureAbsoluteUrl(orgProfile.twitter_url)} target="_blank" rel="noopener noreferrer" className="text-meritum-teal hover:text-meritum-navy transition" data-testid="org-twitter-link"><Globe size={18} /></a>}
                        {orgProfile.facebook_url && <a href={ensureAbsoluteUrl(orgProfile.facebook_url)} target="_blank" rel="noopener noreferrer" className="text-meritum-teal hover:text-meritum-navy transition" data-testid="org-facebook-link"><Globe size={18} /></a>}
                      </div>
                    )}
                  </div>
                  {!isEditingProfile ? (
                    <Button onClick={() => setIsEditingProfile(true)} className="w-full bg-meritum-teal text-white hover:bg-meritum-navy font-body" data-testid="edit-org-profile-btn">
                      <Edit2 size={16} className="mr-2" />Edit Profile
                    </Button>
                  ) : (
                    <div className="space-y-2">
                      <Button onClick={handleSaveOrgProfile} disabled={isSavingProfile} className="w-full bg-meritum-gold text-meritum-navy hover:bg-meritum-gold/90 font-body font-bold" data-testid="save-org-profile-btn">
                        <Save size={16} className="mr-2" />{isSavingProfile ? 'Saving...' : 'Save Profile'}
                      </Button>
                      <Button onClick={() => setIsEditingProfile(false)} variant="outline" className="w-full border-slate-300 text-slate-600 hover:bg-slate-100 font-body">Cancel</Button>
                    </div>
                  )}
                </div>
              </div>

              {/* Right - Profile Details */}
              <div className="lg:col-span-2 space-y-6">
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <h3 className="font-heading text-lg font-semibold text-meritum-navy mb-4">Organization Details</h3>
                  <div className="space-y-4">
                    <div>
                      <Label className="font-body text-sm font-medium text-meritum-navy">Company Name</Label>
                      {isEditingProfile ? <Input value={orgProfile.company_name} onChange={(e) => setOrgProfile(p => ({ ...p, company_name: e.target.value }))} placeholder="Your company name" className="mt-1" data-testid="org-company-name-input" /> : <p className="font-body text-slate-600 mt-1">{orgProfile.company_name || 'Not set'}</p>}
                    </div>
                    <div>
                      <Label className="font-body text-sm font-medium text-meritum-navy">Industry</Label>
                      {isEditingProfile ? <Input value={orgProfile.industry} onChange={(e) => setOrgProfile(p => ({ ...p, industry: e.target.value }))} placeholder="e.g., Technology, Finance, Mining" className="mt-1" data-testid="org-industry-input" /> : <p className="font-body text-slate-600 mt-1">{orgProfile.industry || 'Not set'}</p>}
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label className="font-body text-sm font-medium text-meritum-navy">Country</Label>
                        {isEditingProfile ? <Input value={orgProfile.country} onChange={(e) => setOrgProfile(p => ({ ...p, country: e.target.value }))} placeholder="e.g., South Africa" className="mt-1" data-testid="org-country-input" /> : <p className="font-body text-slate-600 mt-1">{orgProfile.country || 'Not set'}</p>}
                      </div>
                      <div>
                        <Label className="font-body text-sm font-medium text-meritum-navy">City</Label>
                        {isEditingProfile ? <Input value={orgProfile.city} onChange={(e) => setOrgProfile(p => ({ ...p, city: e.target.value }))} placeholder="e.g., Johannesburg" className="mt-1" data-testid="org-city-input" /> : <p className="font-body text-slate-600 mt-1">{orgProfile.city || 'Not set'}</p>}
                      </div>
                    </div>
                    <div>
                      <Label className="font-body text-sm font-medium text-meritum-navy">Website</Label>
                      {isEditingProfile ? <Input value={orgProfile.website} onChange={(e) => setOrgProfile(p => ({ ...p, website: e.target.value }))} placeholder="https://yourcompany.com" className="mt-1" data-testid="org-website-input" /> : (
                        <p className="font-body text-slate-600 mt-1">
                          {orgProfile.website ? <a href={ensureAbsoluteUrl(orgProfile.website)} target="_blank" rel="noopener noreferrer" className="text-meritum-teal hover:underline inline-flex items-center gap-1">{orgProfile.website}<ExternalLink size={12} /></a> : 'Not set'}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label className="font-body text-sm font-medium text-meritum-navy">Company Size</Label>
                      {isEditingProfile ? <Input value={orgProfile.company_size} onChange={(e) => setOrgProfile(p => ({ ...p, company_size: e.target.value }))} placeholder="e.g., 1-10, 11-50, 51-200, 201-500, 500+" className="mt-1" data-testid="org-size-input" /> : <p className="font-body text-slate-600 mt-1">{orgProfile.company_size || 'Not set'}</p>}
                    </div>
                    <div>
                      <Label className="font-body text-sm font-medium text-meritum-navy">Description</Label>
                      {isEditingProfile ? <Textarea value={orgProfile.description} onChange={(e) => setOrgProfile(p => ({ ...p, description: e.target.value }))} placeholder="Tell candidates about your organization..." rows={4} className="mt-1 resize-none" data-testid="org-description-input" /> : <p className="font-body text-slate-600 mt-1 whitespace-pre-wrap">{orgProfile.description || 'No description added yet'}</p>}
                    </div>
                  </div>
                </div>

                {/* Social Media Links */}
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <h3 className="font-heading text-lg font-semibold text-meritum-navy mb-4 flex items-center gap-2">
                    <Globe size={18} className="text-meritum-teal" />Social Media & Online Presence
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <Label className="font-body text-sm font-medium text-meritum-navy flex items-center gap-2"><Linkedin size={14} className="text-[#0077B5]" />LinkedIn</Label>
                      {isEditingProfile ? <Input value={orgProfile.linkedin_url} onChange={(e) => setOrgProfile(p => ({ ...p, linkedin_url: e.target.value }))} placeholder="https://linkedin.com/company/yourcompany" className="mt-1" data-testid="org-linkedin-input" /> : (
                        <p className="font-body text-slate-600 mt-1">{orgProfile.linkedin_url ? <a href={ensureAbsoluteUrl(orgProfile.linkedin_url)} target="_blank" rel="noopener noreferrer" className="text-meritum-teal hover:underline inline-flex items-center gap-1">{orgProfile.linkedin_url}<ExternalLink size={12} /></a> : 'Not set'}</p>
                      )}
                    </div>
                    <div>
                      <Label className="font-body text-sm font-medium text-meritum-navy flex items-center gap-2"><span className="font-bold text-sm">X</span>Twitter / X</Label>
                      {isEditingProfile ? <Input value={orgProfile.twitter_url} onChange={(e) => setOrgProfile(p => ({ ...p, twitter_url: e.target.value }))} placeholder="https://x.com/yourcompany" className="mt-1" data-testid="org-twitter-input" /> : (
                        <p className="font-body text-slate-600 mt-1">{orgProfile.twitter_url ? <a href={ensureAbsoluteUrl(orgProfile.twitter_url)} target="_blank" rel="noopener noreferrer" className="text-meritum-teal hover:underline inline-flex items-center gap-1">{orgProfile.twitter_url}<ExternalLink size={12} /></a> : 'Not set'}</p>
                      )}
                    </div>
                    <div>
                      <Label className="font-body text-sm font-medium text-meritum-navy flex items-center gap-2"><Globe size={14} className="text-[#1877F2]" />Facebook</Label>
                      {isEditingProfile ? <Input value={orgProfile.facebook_url} onChange={(e) => setOrgProfile(p => ({ ...p, facebook_url: e.target.value }))} placeholder="https://facebook.com/yourcompany" className="mt-1" data-testid="org-facebook-input" /> : (
                        <p className="font-body text-slate-600 mt-1">{orgProfile.facebook_url ? <a href={ensureAbsoluteUrl(orgProfile.facebook_url)} target="_blank" rel="noopener noreferrer" className="text-meritum-teal hover:underline inline-flex items-center gap-1">{orgProfile.facebook_url}<ExternalLink size={12} /></a> : 'Not set'}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Sticky Bottom Save Bar (visible during org profile edit) */}
      {isEditingProfile && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-slate-200 shadow-[0_-4px_12px_rgba(0,0,0,0.06)] py-3 px-4" data-testid="sticky-save-bar-org">
          <div className="container mx-auto max-w-7xl flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              {autosaveStatus === 'saving' && (
                <><Loader2 size={14} className="text-meritum-teal animate-spin flex-shrink-0" /><span className="font-body text-xs sm:text-sm text-slate-500 truncate">Auto-saving...</span></>
              )}
              {autosaveStatus === 'saved' && (
                <><CheckCircle2 size={14} className="text-emerald-600 flex-shrink-0" /><span className="font-body text-xs sm:text-sm text-emerald-600 truncate">Auto-saved</span></>
              )}
              {!autosaveStatus && (
                <span className="font-body text-xs sm:text-sm text-slate-400 truncate">Changes save automatically</span>
              )}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button onClick={() => setIsEditingProfile(false)} variant="outline" size="sm" className="border-slate-300 text-slate-600 hover:bg-slate-50 font-body" data-testid="bottom-cancel-btn-org">Done</Button>
              <Button onClick={handleSaveOrgProfile} disabled={isSavingProfile} size="sm" className="bg-meritum-gold text-meritum-navy hover:bg-meritum-gold/90 font-body font-bold" data-testid="bottom-save-btn-org">
                <Save size={14} className="mr-1" />{isSavingProfile ? 'Saving...' : 'Save Now'}
              </Button>
            </div>
          </div>
        </div>
      )}
      {isEditingProfile && <div className="h-20" />}

      {/* Candidate Profile Modal */}
    </div>
  );
}
